import type { LogEntry, SignedRequest } from '../../../src/core/types.js';
import { zec, tokens, short } from '../format.js';
import { withdrawalAddress } from '../wallet.js';

/**
 * A trade receipt. The sequencer signed this exact entry at this exact
 * position in the log; if the operator ever publishes something else at the
 * same number, this slip proves it.
 */
export function Receipt({ entry, symbol, printing }: { entry: LogEntry; symbol?: string; printing?: boolean }) {
  const req = (entry.data as SignedRequest).req;
  const r = entry.result as Record<string, any>;
  const body = req?.body;
  const lines: [string, string][] = [];
  let title = 'Receipt';
  const allFees = () => BigInt(r.fee) + BigInt(r.creatorFee) + BigInt(r.poolFee ?? '0');
  if (body?.type === 'buy' && r.batched) {
    title = `Launch order: $${symbol ?? body.token}`;
    lines.push(['Put in', `${zec(r.zat, 8)} ZEC`], ['Your total', `${zec(r.committed, 8)} ZEC`], ['Fills at', new Date(r.fillsAt).toLocaleTimeString()], ['Price', 'one price for every buyer']);
  } else if (body?.type === 'buy') {
    title = `Bought $${symbol ?? body.token}`;
    lines.push(['Paid', `${zec(r.zatUsed, 8)} ZEC`], ['Received', `${tokens(r.tokensOut)} ${symbol ?? ''}`], ['Fee', `${zec(allFees(), 8)} ZEC`]);
    if (r.poolFee && r.poolFee !== '0') lines.push(['of it anti-snipe, kept by the coin', `${zec(r.poolFee, 8)} ZEC`]);
    if (r.refund !== '0') lines.push(['Refunded', `${zec(r.refund, 8)} ZEC`]);
    if (r.graduated) lines.push(['Graduated', 'yes']);
  } else if (body?.type === 'sell') {
    title = `Sold $${symbol ?? body.token}`;
    lines.push(['Sold', `${tokens(r.tokensIn)} ${symbol ?? ''}`], ['Received', `${zec(r.zatOut, 8)} ZEC`], ['Fee', `${zec(allFees(), 8)} ZEC`]);
    if (r.poolFee && r.poolFee !== '0') lines.push(['of it anti-snipe, kept by the coin', `${zec(r.poolFee, 8)} ZEC`]);
  } else if (body?.type === 'create') {
    title = `Launched $${body.symbol}`;
    lines.push(['Coin', r.token], ['Launch fee', `${zec(r.createFee, 8)} ZEC`]);
    if (r.buy) lines.push(['First buy', `${tokens(r.buy.tokensOut)} for ${zec(r.buy.zatUsed, 8)} ZEC`]);
  } else if (body?.type === 'withdraw') {
    title = 'Withdrawal requested';
    const to = withdrawalAddress(body.commit);
    lines.push(['Sending', `${zec(r.sendZat, 8)} ZEC`], ['Network fee', `${zec(r.fee, 8)} ZEC`], ['To', to ? short(to, 10) : 'sealed']);
  } else if (body?.type === 'note_mint') {
    title = 'Made private';
    lines.push(['Notes', `${r.count} × ${zec(body.denom, 8)} ZEC`]);
  } else if (body?.type === 'note_redeem') {
    title = 'Private ZEC moved in';
    lines.push(['Notes', `${r.count} × ${zec(body.denom, 8)} ZEC`]);
  } else if (body?.type === 'register') {
    title = 'Account opened';
    lines.push(['Deposit slot', String(r.depositIndex)]);
  }
  return (
    <div class={`receipt${printing ? ' printing' : ''}`}>
      <h4>{title}</h4>
      {lines.map(([k, v]) => (
        <div class="ln" key={k}>
          <span>{k}</span>
          <span>{v}</span>
        </div>
      ))}
      <hr />
      <div class="ln">
        <span>Entry</span>
        <span>#{entry.seq}</span>
      </div>
      <div class="ln">
        <span>Time</span>
        <span>{new Date(entry.ts).toLocaleString()}</span>
      </div>
      <hr />
      <div class="hash">hash {entry.hash}</div>
      <div class="hash">signed {short(entry.sig, 16)}</div>
    </div>
  );
}
